// src/pages/components/Checklist.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Pencil, Trash2, Check, X,
  CheckCircle2, ListPlus, Sparkles,
} from 'lucide-react';
import { tasksApi, TaskChecklistItem } from '../api/tasks';
import todoApi from '../api/todo';

interface ChecklistProps {
  taskId: string;
  items: TaskChecklistItem[];
  onRefresh: () => void;
  parentTaskTitle?: string;
}

export default function Checklist({ taskId, items, onRefresh, parentTaskTitle }: ChecklistProps) {
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // Local optimistic copy of items — keeps the checklist responsive without
  // forcing the parent page to refetch on every toggle/edit/delete.
  const [localItems, setLocalItems] = useState<TaskChecklistItem[]>(items);
  useEffect(() => { setLocalItems(items); }, [items]);

  const sortedItems = useMemo(
    () => [...localItems].sort((a, b) => a.order_index - b.order_index),
    [localItems]
  );
  const completedCount = localItems.filter((i) => i.is_done).length;
  const allSelected = sortedItems.length > 0 && selected.size === sortedItems.length;
  const hasSelection = selected.size > 0;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(sortedItems.map((i) => i.id)));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    setAdding(true);
    try {
      const r = await tasksApi.addChecklistItem(taskId, { item: newItem });
      const created = r?.data?.item || r?.data;
      // Optimistic insert — append the new row locally without a page refresh.
      if (created && created.id) {
        setLocalItems((prev) => [...prev, created as TaskChecklistItem]);
      } else {
        // Fallback: best-effort local stub until the next props sync.
        setLocalItems((prev) => [
          ...prev,
          {
            id: `tmp-${Date.now()}`,
            task_id: taskId,
            item: newItem,
            order_index: (prev[prev.length - 1]?.order_index ?? prev.length) + 1,
            is_done: false,
          } as any as TaskChecklistItem,
        ]);
      }
      setNewItem('');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add item');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (itemId: string, isDone: boolean) => {
    // Optimistic flip
    setLocalItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, is_done: !isDone } : i)));
    try {
      await tasksApi.updateChecklistItem(itemId, { is_done: !isDone });
    } catch (err: any) {
      // Rollback
      setLocalItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, is_done: isDone } : i)));
      toast.error(err?.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleDelete = async (itemId: string) => {
    const snapshot = localItems;
    // Optimistic remove
    setLocalItems((prev) => prev.filter((i) => i.id !== itemId));
    setSelected((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
    try {
      await tasksApi.deleteChecklistItem(itemId);
    } catch (err: any) {
      setLocalItems(snapshot);
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const startEdit = (item: TaskChecklistItem) => {
    setEditingId(item.id);
    setEditText(item.item);
  };

  const saveEdit = async () => {
    if (!editingId || !editText.trim()) {
      setEditingId(null);
      return;
    }
    const id = editingId;
    const text = editText.trim();
    const snapshot = localItems;
    // Optimistic rename
    setLocalItems((prev) => prev.map((i) => (i.id === id ? { ...i, item: text } : i)));
    setEditingId(null);
    setEditText('');
    try {
      await tasksApi.updateChecklistItem(id, { item: text });
      toast.success('Item updated');
    } catch (err: any) {
      setLocalItems(snapshot);
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
  };

  // Mark all selected items as completed
  const markSelectedComplete = async () => {
    if (!hasSelection) return;
    setBusy(true);
    const ids = Array.from(selected);
    const snapshot = localItems;
    setLocalItems((prev) => prev.map((i) => (ids.includes(i.id) ? { ...i, is_done: true } : i)));
    setSelected(new Set());
    try {
      await Promise.all(ids.map((id) => tasksApi.updateChecklistItem(id, { is_done: true })));
      toast.success(`${ids.length} item${ids.length === 1 ? '' : 's'} marked complete`);
    } catch (err: any) {
      setLocalItems(snapshot);
      toast.error(err?.response?.data?.detail || 'Failed to mark complete');
    } finally {
      setBusy(false);
    }
  };

  // Create a personal to-do for each selected checklist item.
  // Title = parent task title (or fallback). Notes/description = the checklist item text.
  const addSelectedAsTodos = async () => {
    if (!hasSelection) return;
    setBusy(true);
    try {
      const picked = sortedItems.filter((i) => selected.has(i.id));
      const title = parentTaskTitle || 'Task checklist';
      await Promise.all(
        picked.map((it) =>
          todoApi.quickAdd({
            title,
            description: it.item,
            priority: 'MEDIUM',
            status: 'PENDING',
            source_task_id: taskId,
            source_checklist_item_id: it.id,
          } as any)
        )
      );
      toast.success(`${picked.length} to-do${picked.length === 1 ? '' : 's'} added to your planner`);
      setSelected(new Set());
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to add to-dos');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gt-card p-5 mb-6" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div style={{
            width: 36, height: 36, borderRadius: 11,
            background: 'var(--gt-grad-aurora)',
            color: '#ffffff',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'var(--gt-glow-soft), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}>
            <ClipboardList className="w-4 h-4" />
          </div>
          <div>
            <h3 className="gt-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--gt-text-primary)', lineHeight: 1.2 }}>Checklist</h3>
            {items.length > 0 && (
              <span className="gt-page-sub gt-numerals" style={{ fontSize: 11.5, marginTop: 2, display: 'block' }}>
                <span className="gt-display" style={{ color: 'var(--gt-accent-strong)' }}>{completedCount}</span>
                {' '}of <span className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>{items.length}</span> completed
              </span>
            )}
          </div>
        </div>

        {/* Selection toolbar */}
        {sortedItems.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={toggleSelectAll} className="gt-btn is-ghost is-sm" disabled={busy}>
              <span style={{
                width: 16, height: 16, borderRadius: 4,
                border: `2px solid ${allSelected ? 'var(--gt-primary)' : 'var(--gt-border-strong)'}`,
                background: allSelected ? 'var(--gt-primary)' : 'transparent',
                color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {allSelected && <Check className="w-2.5 h-2.5" strokeWidth={4} />}
              </span>
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>

            {hasSelection && (
              <>
                <span className="gt-pill is-personal gt-numerals">{selected.size} selected</span>
                <button onClick={markSelectedComplete} className="gt-btn is-accent is-sm" disabled={busy}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Mark complete
                </button>
                <button onClick={addSelectedAsTodos} className="gt-btn is-primary is-sm" disabled={busy}>
                  <ListPlus className="w-3.5 h-3.5" />
                  Add as To-Do
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add new item */}
      <form onSubmit={handleAdd} className="flex gap-2 mb-4">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add a new item…"
          className="gt-input flex-1"
        />
        <button
          type="submit"
          disabled={adding || !newItem.trim()}
          className="gt-btn is-primary"
        >
          <Plus className="w-4 h-4" />
          {adding ? 'Adding…' : 'Add Item'}
        </button>
      </form>

      {/* Items */}
      <div className="space-y-2 max-h-96 overflow-y-auto gt-scroll" style={{ paddingRight: 4 }}>
        {sortedItems.length === 0 ? (
          <div className="gt-empty">
            <div className="gt-empty-leaf"><Sparkles className="w-6 h-6" /></div>
            <div className="gt-empty-title">No checklist items</div>
            <div className="gt-page-sub">Add items to <em className="gt-serif-italic">track subtasks</em>.</div>
          </div>
        ) : (
          sortedItems.map((item) => {
            const isSelected = selected.has(item.id);
            const isEditing = editingId === item.id;
            return (
              <div
                key={item.id}
                className="group flex items-center gap-3"
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: isSelected ? 'var(--gt-primary-container)' : 'var(--gt-surface-1)',
                  border: `1px solid ${isSelected ? 'rgba(16, 185, 129, 0.45)' : 'var(--gt-divider)'}`,
                  transition: 'all 160ms cubic-bezier(0.33,1,0.68,1)',
                }}
              >
                {/* Selection checkbox */}
                <button
                  type="button"
                  onClick={() => toggleSelect(item.id)}
                  aria-label="Select"
                  style={{
                    width: 18, height: 18, borderRadius: 5,
                    border: `2px solid ${isSelected ? 'var(--gt-primary)' : 'var(--gt-border-strong)'}`,
                    background: isSelected ? 'var(--gt-primary)' : 'transparent',
                    color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    cursor: 'pointer',
                    transition: 'all 160ms',
                  }}
                >
                  {isSelected && <Check className="w-2.5 h-2.5" strokeWidth={4} />}
                </button>

                {/* Done toggle (circle) */}
                <button
                  type="button"
                  onClick={() => handleToggle(item.id, item.is_done)}
                  aria-label="Toggle done"
                  style={{
                    width: 22, height: 22, borderRadius: 999,
                    border: `2px solid ${item.is_done ? 'var(--gt-accent-strong)' : 'var(--gt-border-strong)'}`,
                    background: item.is_done ? 'var(--gt-accent-strong)' : 'transparent',
                    color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                >
                  {item.is_done && <Check className="w-3 h-3" strokeWidth={3} />}
                </button>

                {/* Item text / edit input */}
                {isEditing ? (
                  <input
                    autoFocus
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
                    onBlur={saveEdit}
                    className="gt-input flex-1"
                    style={{ padding: '6px 10px', fontSize: 13 }}
                  />
                ) : (
                  <span className="flex-1 gt-display" style={{
                    fontSize: 13.5, fontWeight: 500,
                    color: item.is_done ? 'var(--gt-text-disabled)' : 'var(--gt-text-primary)',
                    textDecoration: item.is_done ? 'line-through' : 'none',
                  }}>
                    {item.item}
                  </span>
                )}

                {/* Inline action icons */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isEditing ? (
                    <>
                      <button onMouseDown={(e) => { e.preventDefault(); saveEdit(); }} className="gt-icon-btn is-sm" aria-label="Save">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onMouseDown={(e) => { e.preventDefault(); setEditingId(null); }} className="gt-icon-btn is-sm" aria-label="Cancel">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(item)} className="gt-icon-btn is-sm" aria-label="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="gt-icon-btn is-sm" style={{ color: 'var(--gt-dnd)' }} aria-label="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer celebration */}
      {items.length > 0 && completedCount === items.length && (
        <div className="mt-4 p-3" style={{
          background: 'linear-gradient(135deg, rgba(167, 243, 208, 0.55) 0%, rgba(209, 250, 229, 0.45) 100%)',
          border: '1px solid rgba(52, 211, 153, 0.45)',
          borderRadius: 12,
          textAlign: 'center',
        }}>
          <span className="gt-display" style={{ fontSize: 13, fontWeight: 700, color: 'var(--gt-primary-deep)' }}>
            🎉 All <em className="gt-serif-italic">{items.length}</em> items completed!
          </span>
        </div>
      )}
    </div>
  );
}
